/**
 * DESKTOP_THEME — desktop-only design tokens, single source of truth for
 * the desktop website/webapp.
 *
 * DESKTOP ONLY. These values are copy-pasted (not reinvented) from the
 * approved DesktopLandingScreen, so every other desktop page (auth, home,
 * profile, stats, settings, sidebar) pulls from the same palette/
 * typography/radii instead of each screen inventing its own hex values.
 * Reuses mobile's gold (#D4920A, see theme/tokens.ts TOKENS.colors.gold)
 * and gray tokens where they already match the landing page — this is
 * not a new, unrelated color system.
 *
 * Mobile is unaffected: nothing in theme/tokens.ts, index.css, or any
 * mobile screen imports this file, and this file imports nothing mobile.
 */
export const DESKTOP_THEME = {
  color: {
    pageBg:       '#000000',
    heroGradient: 'radial-gradient(ellipse at 50% 0%, rgba(212,146,10,0.06) 0%, rgba(0,0,0,0) 55%), #000',

    // Slightly lighter than before (#1c1c1c → #1e1e1e) so cards read as a
    // distinct raised layer against the pure-black page, giving the
    // "floating over background" feel without looking grey or washed out.
    panelFlat:     '#121212',
    panelGradient: 'linear-gradient(135deg, #1e1e1e 0%, #161616 100%)',
    fieldBg:       '#15151a',

    border:          'rgba(255,255,255,0.08)',
    borderStrong:    'rgba(255,255,255,0.12)',
    borderHoverGold: 'rgba(212,146,10,0.5)',

    // DesktopActionCard "premium interactive surface" — thin gold border at
    // rest, ambient glow; brighter border + stronger glow + lift on hover.
    actionBorder:      'rgba(212,146,10,0.35)',
    actionBorderHover: 'rgba(212,146,10,0.8)',
    actionGlow:        '0 0 24px rgba(212,146,10,0.08)',
    actionGlowHover:   '0 8px 28px rgba(212,146,10,0.18)',

    // DesktopSurfaceCard hover (opt-in via hoverable prop) — a quieter
    // gold-tinted border + faint lift for panels that can be interacted
    // with but don't need the full DesktopActionCard treatment.
    surfaceHoverBorder: 'rgba(212,146,10,0.28)',
    surfaceHoverGlow:   '0 4px 20px rgba(212,146,10,0.07)',

    // DesktopButton "secondary" variant
    secondaryBg:          'rgba(255,255,255,0.04)',
    secondaryBorder:      'rgba(212,146,10,0.30)',
    secondaryBorderHover: 'rgba(212,146,10,0.65)',
    secondaryGlowHover:   '0 0 16px rgba(212,146,10,0.14)',

    // DesktopButton "danger" variant — ghost outline; restrained at rest, clear on hover
    dangerBg:          'rgba(224,82,82,0.06)',
    dangerBorder:      'rgba(224,82,82,0.30)',
    dangerBorderHover: 'rgba(224,82,82,0.60)',
    dangerGlowHover:   '0 0 16px rgba(224,82,82,0.12)',
    dangerText:        '#E05252',

    // Sidebar chrome — bg matches pageBg exactly so the sidebar reads as
    // black, defined only by its gold border rather than a separate panel.
    sidebarBg:       '#000000',
    sidebarBorder:   'rgba(212,146,10,0.18)',
    sidebarActiveBg: 'rgba(212,146,10,0.12)',
    // Neutral translucent reveal on hover (non-active items only)
    sidebarHoverBg:  'rgba(255,255,255,0.05)',

    // DesktopStatCard inner cell backgrounds — previously hardcoded
    statCellBg:     'rgba(255,255,255,0.025)',
    statCellBorder: 'rgba(255,255,255,0.06)',

    // Neutral interactive surface (toggle off-state, dev picker buttons,
    // secondary interactive elements that sit above a panel).
    surfaceElevated: '#2A2A2A',

    gold:           '#D4920A',
    goldOnGoldText: '#1a1305',

    textPrimary:   '#ffffff',
    textSecondary: '#9CA3AF',
    textMuted:     '#6B7280',
    textFaint:     '#444444',
  },

  radius: {
    input:  8,
    button: 10,
    card:   16,
    panel:  16,
  },

  maxWidth: {
    narrow: 640,
    page:   880,
    wide:   1100,
  },

  font: {
    heading: 'Nunito, sans-serif',
    sans:    'Montserrat, sans-serif',
  },
} as const;
